import React, { useState } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/core';
import { Box, HStack, Spinner, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ReturnsText } from '@betterment/shared-components';
import { ParentSize } from '@visx/responsive';

import { DateOptionToggleGroup } from './DateOptionToggleGroup';
import { DirectTradingAccountSecurityPricePerformanceQuery } from './graphql/directTradingAccountSecurityPricePerformance.generated';
import { PerformanceChart } from './PerformanceChart';
import { PerformanceDisclosureDialog } from './PerformanceDisclosureDialog';

import { DateRangeOption } from '../../../../../graphql/schema';
import { PlaceholderPerformanceChart } from '../../../../../Shared/components-v2/placeholder-performance-chart/PlaceholderPerformanceChart';

const timePeriods: { label: string; value: DateRangeOption }[] = [
  { label: '1W', value: DateRangeOption.PastWeek },
  { label: '1M', value: DateRangeOption.PastMonth },
  { label: '3M', value: DateRangeOption.PastThreeMonths },
  { label: '1Y', value: DateRangeOption.PastYear },
  { label: 'All', value: DateRangeOption.AllTime },
];

const timePeriodForValue = (value: DateRangeOption | undefined) => {
  return timePeriods.find(timePeriod => timePeriod.value === value);
};

const formatDateTime = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const part = (type: string) => parts.find(p => p.type === type)?.value;
  const day = `${part('month')} ${part('day')}, ${part('year')}`;
  const time = `${part('hour')}:${part('minute')}${part('dayPeriod')?.toLowerCase()} ET`;
  return `${day} at ${time}`;
};

const overlayStyles = css.raw({
  bg: 'background.surface',
  opacity: '0.4',
});

const centeredSpinnerStyles = css.raw({
  position: 'absolute',
  top: '50%',
  left: '50%',
  translate: '-50% -50%',
  zIndex: '1',
});

type PricePointType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['timeSeries'][number];

type PerformanceSectionProps = {
  latestPriceAmount: Money;
  latestPriceCurrentAsOf: string;
  latestReturnsAmount: Money;
  latestReturnsPercentage: number;
  timeSeries: PricePointType[];
  dateRangeOption: DateRangeOption;
  onDateRangeChange: (option: DateRangeOption) => void;
  isLoading?: boolean;
};

export const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  latestPriceAmount,
  latestPriceCurrentAsOf,
  latestReturnsAmount,
  latestReturnsPercentage,
  timeSeries,
  dateRangeOption,
  onDateRangeChange,
  isLoading = false,
}) => {
  const [highlight, setHighlight] = useState<PricePointType | null>(null);

  const shouldShowDateToggle = timeSeries.length > 0;

  const displayLatestPriceAmount = highlight?.amount || latestPriceAmount;
  const displayLatestReturnsAmount = highlight?.returns.amount || latestReturnsAmount;
  const displayLatestReturnsPercentage = highlight?.returns.percentage || latestReturnsPercentage;
  const dateRangeLabel = timePeriodForValue(dateRangeOption)?.label;

  const chartData: PricePointType[] = (() => {
    if (timeSeries.length > 0) return timeSeries;
    if (latestPriceAmount.cents === 0) return [];
    return [
      {
        __typename: 'FinancialSecurityPricePerformancePricePoint' as const,
        dateTime: new Date(),
        amount: toMoney(0),
        returns: {
          __typename: 'FinancialSecurityPriceReturns' as const,
          amount: toMoney(0),
          percentage: 0,
        },
      },
    ];
  })();

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack>
        <Text color="text" variant="text100">
          {highlight ? formatDateTime(highlight.dateTime) : 'Last price'}
        </Text>
        <Text variant="text400">{formatMoney(displayLatestPriceAmount, 'default')}</Text>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <ReturnsText
            variant="text100"
            negativeToken="text.critical"
            amount={displayLatestReturnsAmount}
            percent={displayLatestReturnsPercentage}
          />
          {!highlight && <Text variant="text100">{dateRangeLabel}</Text>}
        </HStack>
      </VStack>

      <Box>
        {isLoading && <Spinner css={centeredSpinnerStyles} />}
        <Box css={{ ...(isLoading && overlayStyles), width: '100%', height: '100%' }}>
          <ParentSize>
            {({ width }) =>
              chartData.length > 0 ? (
                <PerformanceChart
                  width={width}
                  height={200}
                  data={chartData}
                  disabled={isLoading}
                  onHighlightChange={setHighlight}
                />
              ) : (
                <PlaceholderPerformanceChart width={width} height={200} variant="zero-state" />
              )
            }
          </ParentSize>
        </Box>
      </Box>

      {shouldShowDateToggle && (
        <HStack css={{ justifyContent: 'center' }}>
          <DateOptionToggleGroup value={dateRangeOption} onValueChange={onDateRangeChange}>
            {timePeriods.map(({ label, value: optionValue }) => (
              <DateOptionToggleGroup.Item key={optionValue} value={optionValue}>
                {label}
              </DateOptionToggleGroup.Item>
            ))}
          </DateOptionToggleGroup>
        </HStack>
      )}

      <Text variant="text25" color="text.weak">
        Price as of {latestPriceCurrentAsOf}. See <PerformanceDisclosureDialog />
      </Text>
    </VStack>
  );
};
